/**
 * RadarConfigFields — the editable body of a {@link TrlRadarConfig}.
 *
 * Reused in two places so the game-level defaults and a per-node override are
 * authored with identical controls:
 *   • TrlGameSettingsModal (game-level defaults, under "Game Settings")
 *   • PlanetEditModal (per-node override, seeded from the game defaults)
 *
 * Purely controlled: it renders `value` and reports every edit through
 * `onChange`. A radar config is 1..4 ranges sorted ascending by distance; the
 * FIRST range is always the winning/capture radius, the rest are "near" bands.
 * Each range carries a distance (1..120 m), colour, message and a Proximity
 * toggle (reveal exact metres-to-target). Validation here is advisory — the
 * server re-normalises via `normalizeTrlRadarConfig`.
 */

import {
  TRL_RADAR_MIN_RANGES,
  TRL_RADAR_MAX_RANGES,
  TRL_RADAR_MIN_DISTANCE,
  TRL_RADAR_MAX_DISTANCE,
  TRL_RADAR_COLOR_PRESETS,
  type TrlRadarConfig,
  type TrlRadarRange,
} from '@swtorbit/shared-socket-events';

interface RadarConfigFieldsProps {
  value: TrlRadarConfig;
  onChange: (next: TrlRadarConfig) => void;
  disabled?: boolean;
}

/** Match a hex colour to a preset label, or 'custom' when none match. */
const presetForColor = (color: string): string => {
  const hit = TRL_RADAR_COLOR_PRESETS.find((p) => p.value.toLowerCase() === color.toLowerCase());
  return hit ? hit.value : 'custom';
};

export function RadarConfigFields({ value, onChange, disabled = false }: RadarConfigFieldsProps) {
  const ranges = value.ranges;

  const patchRange = (index: number, patch: Partial<TrlRadarRange>) => {
    onChange({ ranges: ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  };

  const removeRange = (index: number) => {
    if (ranges.length <= TRL_RADAR_MIN_RANGES) return;
    onChange({ ranges: ranges.filter((_, i) => i !== index) });
  };

  const addRange = () => {
    if (ranges.length >= TRL_RADAR_MAX_RANGES) return;
    const last = ranges[ranges.length - 1];
    const nextDistance = Math.min(TRL_RADAR_MAX_DISTANCE, (last?.distanceMeters ?? 0) + 10);
    onChange({
      ranges: [
        ...ranges,
        { distanceMeters: nextDistance, color: '#f1c40f', message: 'Near', showProximity: false },
      ],
    });
  };

  // Advisory validation: strictly-ascending distances within [1, 120].
  const ascendingOk = ranges.every(
    (r, i) => i === 0 || r.distanceMeters > ranges[i - 1].distanceMeters,
  );
  const inBoundsOk = ranges.every(
    (r) => r.distanceMeters >= TRL_RADAR_MIN_DISTANCE && r.distanceMeters <= TRL_RADAR_MAX_DISTANCE,
  );
  const canAdd = !disabled && ranges.length < TRL_RADAR_MAX_RANGES && (ranges[ranges.length - 1]?.distanceMeters ?? 0) < TRL_RADAR_MAX_DISTANCE;

  return (
    <div className="d-flex flex-column gap-xs">
      <span className="fs-xs text-uppercase text-slate">Radar ranges</span>
      <span className="fs-xs text-slate">
        The first range is the winning/capture radius; later ranges are &quot;near&quot; bands. Distances
        must increase and stay at or below {TRL_RADAR_MAX_DISTANCE} m.
      </span>

      {ranges.map((r, i) => {
        const preset = presetForColor(r.color);
        return (
          <div key={i} className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-2xs">
            <div className="d-flex align-items-center justify-content-between">
              <strong className="fs-sm">{i === 0 ? 'Winning range' : `Near band ${i}`}</strong>
              {i >= TRL_RADAR_MIN_RANGES && ranges.length > TRL_RADAR_MIN_RANGES && (
                <button
                  type="button"
                  className="btn btn--sm btn--red"
                  disabled={disabled}
                  onClick={() => removeRange(i)}
                >
                  Remove
                </button>
              )}
            </div>

            <label className="d-flex align-items-center gap-2xs fs-sm">
              Distance
              <input
                type="number"
                className="input"
                style={{ width: '6rem' }}
                min={TRL_RADAR_MIN_DISTANCE}
                max={TRL_RADAR_MAX_DISTANCE}
                step="1"
                value={String(r.distanceMeters)}
                disabled={disabled}
                onChange={(e) => {
                  const raw = parseInt(e.target.value, 10);
                  const distanceMeters = Number.isFinite(raw)
                    ? Math.min(TRL_RADAR_MAX_DISTANCE, Math.max(TRL_RADAR_MIN_DISTANCE, raw))
                    : TRL_RADAR_MIN_DISTANCE;
                  patchRange(i, { distanceMeters });
                }}
              />
              metres
            </label>

            <label className="d-flex align-items-center gap-2xs fs-sm">
              Colour
              <select
                className="input"
                style={{ width: '8rem' }}
                value={preset}
                disabled={disabled}
                onChange={(e) => {
                  if (e.target.value !== 'custom') patchRange(i, { color: e.target.value });
                }}
              >
                {TRL_RADAR_COLOR_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
                <option value="custom">Custom…</option>
              </select>
              <input
                type="color"
                className="input"
                style={{ width: '3rem', padding: 0 }}
                value={r.color}
                disabled={disabled}
                onChange={(e) => patchRange(i, { color: e.target.value })}
              />
            </label>

            <label className="d-flex align-items-center gap-2xs fs-sm">
              Message
              <input
                type="text"
                className="input"
                style={{ flex: 1 }}
                value={r.message}
                disabled={disabled}
                onChange={(e) => patchRange(i, { message: e.target.value })}
              />
            </label>

            <label className="d-flex align-items-center gap-2xs fs-sm">
              <input
                className="checkbox"
                type="checkbox"
                checked={r.showProximity}
                disabled={disabled}
                onChange={(e) => patchRange(i, { showProximity: e.target.checked })}
              />
              <span>Show exact distance to target (Proximity)</span>
            </label>
          </div>
        );
      })}

      {(!ascendingOk || !inBoundsOk) && (
        <span className="fs-xs text-red">
          Distances must strictly increase and stay between {TRL_RADAR_MIN_DISTANCE} and{' '}
          {TRL_RADAR_MAX_DISTANCE} m.
        </span>
      )}

      <div>
        <button type="button" className="btn btn--sm btn--outline btn--slate" disabled={!canAdd} onClick={addRange}>
          + Add range
        </button>
      </div>
    </div>
  );
}
